import { useCallback } from "react";
import { useQuery, useMutation } from "./urqlHooks";
import { useSelectedCompanyId } from "./tenantCompanyId";

const GET_NEW_NOMINA = `query GetProvidersToPay($companyId: String!){
    getProvidersToPay(companyId:$companyId) {
      providers
      totalSelected
      totalToPay
    }
}`;

export const GetNomina = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_NEW_NOMINA,
    variables: { companyId },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const SELECT_PARTIAL_NOMINA = `mutation SelectPartialNomina($input: [SelectDocumentNominaInput!]!, $companyId: String!){
  selectDocumentsForNomina(input: $input, companyId: $companyId){
    status
    message
    data
  }
}`;

export const SelectPartialNomina = () => {
  const [result, executeMutation] = useMutation(SELECT_PARTIAL_NOMINA);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const SELECT_ALL_NOMINA = `mutation SelectAllDocsNomina($input: [SelectDocumentNominaInput!]!, $companyId: String!){
  multiSelectDocForNomina(input: $input, companyId: $companyId){
    status
    message
    data
  }
}`;

export const SelectAllDocsNomina = () => {
  const [result, executeMutation] = useMutation(SELECT_ALL_NOMINA);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_DOCUMENTS_TO_NOMINA = `query GetSelectedBySelectorNominas($companyId: String!){
  getSelectedBySelectorNominas(companyId: $companyId){
    userId
    userName
    userLastName
    userEmail
    selected{
      id
      typeLabel
      selectionId
      accountId
      amountSelected
      dateSelected
      issuerId
      issuerName
      documentId
      documentCode
      documentType
      selectorId
      agenda {
        id
        bankAccounts {
          id
          bankAccount
          bankAccountType
          bankRut
          bankId
          email
          isMain
        }
      }
    }
    }
}`;

export const GetSelectedBySelectorNominas = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_DOCUMENTS_TO_NOMINA,
    variables: { companyId },
    requestPolicy: "network-only"
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_NOMINA = `mutation createNominaNew($input: NominasNewInput!, $companyId: String!) {
  createNomina(input: $input, companyId: $companyId) {
    id
    name
    companyId
    status
  }
}`;

export const CreateNomina = () => {
  const [, createNewNomina] = useMutation(CREATE_NOMINA);
  return createNewNomina;
};

const GET_IN_PROCESS_NOMINA = `query GetInProcessNomina($companyId: String!, $status: [NominaStatus!]!, $pagination: PaginationInput!){
  getNominasByCompany(
    companyId: $companyId,
    status: $status
    pagination : $pagination
  ){
    count
    limit
    page
    nominas {
     id
    name
    companyId
    updatedAt
    createdAt
    isUpdating
    reprocess {
      state
      date
      user {
        id
        email
        name
      }
    }
    errorHistory {
      message
      date
      code
    }
    selectedDocuments{
      id
      userId
      documentId
      amountSelected
      matchedAmount
      approvalStatus
      isHidden
      editionHistory {
        date
        userId
        userEmail
        event
      }
      isMatched
      selectedDTEDetail{
        iconAgenda
        agendaId
        issuerId
        issuerName
        documentId
        documentCode
        documentType
        typeLabel
        summary
        dueDate
        originalDueDate
        paymentDate
        isPaid
        remaining
        agenda {
          address
          isClient
          isProvider
          bankRut
          bankId
          name
          rut
          email
          bankAccount
          bankAccountType
          paymentDaysClient
          paymentDaysProvider
        }
      }
    }
    editionHistory{
      date
      userId
      event
    }
    userRegistry{
      creatorId
      creatorEmail
    }
    status
    bankPasswordDetail{
      id
      templateId
      name
      description
      content
      
    }
    creatorDetail{
      firstName
      lastName
      email
      companyRole
    } 
    }
  }
}`;

export const GetInProcessNomina = ({ companyId, status, pagination }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_IN_PROCESS_NOMINA,
    variables: { companyId, status, pagination },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const REMOVE_SELECTION = `mutation RemoveSelection($input: [RemoveSelectionDocumentInput!]!, $deleteOnlyCurrentUser: Boolean!, $companyId: String!) {
  removeSelectionFromDocs(input: $input, deleteOnlyCurrentUser: $deleteOnlyCurrentUser, companyId: $companyId) {
    status
    message
    data
  }
}`;

export const RemoveSelection = () => {
  const [, removeSelection] = useMutation(REMOVE_SELECTION);
  return removeSelection;
};

const ADD_DOCUMENTS_NOMINA = `mutation AddDocumentsNomina($nominaId: String!, $companyId: String!, $newDocuments: [SelectedDTEInput!]!) {
  addDocumentsNomina(nominaId: $nominaId, companyId: $companyId, newDocuments: $newDocuments){
    status
    message
    data
  }
}`;

export const AddDocumentsNomina = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, addDocumentsToNomina] = useMutation(ADD_DOCUMENTS_NOMINA);
  return useCallback(
    (vars) => addDocumentsToNomina({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [addDocumentsToNomina, selectedCompanyId],
  );
};

const DISCARD_NOMINA = `mutation DiscardNomina($nominaId: String!, $companyId: String!) {
  discardNomina(nominaId: $nominaId, companyId: $companyId) {
    status
    message
    data
  }
}`

export const DiscardNomina = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, discardNomina] = useMutation(DISCARD_NOMINA);
  return useCallback(
    (vars) => discardNomina({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [discardNomina, selectedCompanyId],
  );
};

const REMOVE_DOCUMENT_NOMINA = `mutation RemoveDocumentsNomina($nominaId: String!, $companyId: String!, $selectionId: [String!]!) {
  removeDocumentsNomina(nominaId: $nominaId, companyId: $companyId, selectionId: $selectionId){
    status
    message
    data
  }
}`

export const RemoveDocumentNomina = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, removeDocumentNomina] = useMutation(REMOVE_DOCUMENT_NOMINA);
  return useCallback(
    (vars) => removeDocumentNomina({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [removeDocumentNomina, selectedCompanyId],
  );
};

const UPDATE_PROVIDER_LABEL = `mutation UpdateProviderLabel($providerIds: [String!]!, $label: String!, $companyId: String!){
  updateProviderLabel(providerIds: $providerIds, label: $label, companyId: $companyId) {
    status
    message
    data
  }
}`

export const UpdateProviderLabel = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, updateProviderLabel] = useMutation(UPDATE_PROVIDER_LABEL);
  return useCallback(
    (vars) => updateProviderLabel({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [updateProviderLabel, selectedCompanyId],
  );
};

const UPDATE_NOMINA = `mutation UpdateNominaInfo($nominaId: String!, $companyId: String!, $input: NominasNewInput!) {
  updateNominaInfo(nominaId: $nominaId, companyId: $companyId, input: $input) {
    status
    message
    data
  }
}`

export const UpdateNominaApi = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, updateNominaApi] = useMutation(UPDATE_NOMINA);
  return useCallback(
    (vars) => updateNominaApi({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [updateNominaApi, selectedCompanyId],
  );
}

const MODIFY_NOMINA = `mutation ModifyNomina(
  $companyId: String!, 
  $input: [DocumentToModify!]!,
  $action: EventType,
  ) {
  modifyNomina(companyId: $companyId, input: $input, action: $action) {
      ok
      nModified
  }
}`

export const ModifyNomina = () => {
  const [, modifyNomina] = useMutation(MODIFY_NOMINA);
  return modifyNomina;
}

const DOWNLOAD_NOMINA = `mutation DownladNomina($nominaId: String! $companyId: String! $isExcelNeeded: Boolean) {
  downloadNewNomina(nominaId: $nominaId, companyId: $companyId, isExcelNeeded: $isExcelNeeded)
}`

export const DownloadNomina = () => {
  const [, downloadNomina] = useMutation(DOWNLOAD_NOMINA);
  return downloadNomina;
}

const PROCESS_NOMINA = `mutation ProcessNomina($nominaId: String! $companyId: String! $input: [ProcessingDocumentsInput!]! $isBot: Boolean!) {
  processNomina(nominaId: $nominaId, companyId: $companyId, input: $input, isBot: $isBot) {
    status
    message
    data
  }
}`

export const ProcessNomina = () => {
  const [, processNomina] = useMutation(PROCESS_NOMINA);
  return processNomina;
}

const UPDATE_NOMINAS_FILTER = `mutation UpdateNominasLabelFilter($labels: [String!]!) {
  updateNominasLabelFilter(labels: $labels) {
    status
    message
    data
  }
}`

export const UpdateNominaFilter = () => {
  const [, filteredNomina] = useMutation(UPDATE_NOMINAS_FILTER);
  return filteredNomina;
}

const AUTO_INFORM_NOMINA = `mutation AutoInformNomina($nominaId: String! $companyId: String!) {
  autoInformNomina(nominaId: $nominaId, companyId: $companyId) {
    status
    message
    data
  }
}`

export const AutoInformNomina = () => {
  const [, autoInformNomina] = useMutation(AUTO_INFORM_NOMINA);
  return autoInformNomina;
}

// Nueva mutación: revertir nómina (dispara N8N para eliminar ticket en HubSpot)
const AUTO_REVERT_NOMINA = `mutation AutoRevertNomina($nominaId: String! $companyId: String!) {
  autoRevertNomina(nominaId: $nominaId, companyId: $companyId) {
    status
    message
    data
  }
}`

export const AutoRevertNomina = () => {
  const [, autoRevertNomina] = useMutation(AUTO_REVERT_NOMINA);
  return autoRevertNomina;
}

const UPDATE_NOMINA_STATUS = `mutation CheckNominasStatusBank($nominaId: String!, $companyId: String!) {
  checkNominasStatusBank(nominaId: $nominaId, companyId: $companyId) {
    status
    message
    data
  }
}`

export const UpdateNominaStatus = () => {
  const [, updateNominaStatus] = useMutation(UPDATE_NOMINA_STATUS);
  return updateNominaStatus;
}

const SETTLETAXDOCUMENTNOMINAS = `mutation SETTLE_TAX_DOCUMENT_NOMINAS(
  $id: String!
  $companyId: String!
  $isPaid: Boolean!
  $remaining: String!
) {
  settleTaxDocumentNominas(id: $id, companyId: $companyId, isPaid: $isPaid, remaining: $remaining) {
    status
    message
    data
  }
}`;

export const SettleTaxDocumentNominas = () => {
  const [, settleTaxDocumentNominas] = useMutation(
    SETTLETAXDOCUMENTNOMINAS
  );
  return settleTaxDocumentNominas;
};
const GET_NOMINAS_BY_PASSWORD = `query GetNominasByPassword($passwordId: String!, $companyId: String!) {
  getNominasByPasswordId(passwordId: $passwordId, companyId: $companyId) {
    nominasToUpdate {
      id
      bankPasswordTemplateId
      bankPasswordId
      name
      companyId
      isUpdating
      status
      userRegistry {
        creatorId
        creatorEmail
      }
    }
    nominasToDelete {
      id
      bankPasswordTemplateId
      bankPasswordId
      name
      companyId
      isUpdating
      status
      userRegistry {
        creatorId
        creatorEmail
      }
    }
  }
}`

export const GetNominasByPassword = ({ passwordId }) => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, reexecuteQuery] = useQuery({
    query: GET_NOMINAS_BY_PASSWORD,
    variables: { passwordId, companyId: selectedCompanyId },
    pause: !passwordId || !selectedCompanyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
}

const UPDATE_NOMINAS_ON_DELETE = `mutation UpdateNomoinasOnDeletePassword($nominasArr: [UpdateNominasOnDelete!]!, $companyId: String!) {
  updateNominasOnErasePassword(nominasArr: $nominasArr, companyId: $companyId)
}`

export const UpdateNominasOnDeletePassword = () => {
  const [, updateNominaOnDelete] = useMutation(UPDATE_NOMINAS_ON_DELETE);
  return updateNominaOnDelete
}

const CREATE_PAGO_PROVEEDOR_COMMENT = `
  mutation createPagoProveedorComment($agendaId: String!, $input: AgendaCollectionCommentInput!, $companyId: String!) {
    createPagoProveedorComment(agendaId: $agendaId, input: $input, companyId: $companyId) {
      id
    }
  }
`;

export const CreatePagoProveedorComment = () => {
  const [result, executeMutation] = useMutation(CREATE_PAGO_PROVEEDOR_COMMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};
